import { Navigate, useSearchParams } from 'react-router-dom';
import Link from '@/components/smart-link';
import { MagicLinkForm } from '@/components/auth/magic-link-form';
import { WaitlistForm } from '@/components/auth/waitlist-form';
import { useCurrentUser } from '@/hooks/use-current-user-org';
import { trpc } from '@/lib/trpc';
import { LoadingSpinner, SharpeLogo } from '@sharpe/ui/components';
import { motion } from 'framer-motion';
import ColorBends from '@/components/auth/ColorBends';

export default function SignUpPage() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, sessionStatus } = useCurrentUser();
  const providerQuery = trpc.auth.provider.useQuery(undefined, { staleTime: 5 * 60 * 1000 });
  const configQuery = trpc.config.get.useQuery(undefined, { staleTime: 5 * 60 * 1000 });

  const initialEmail = searchParams.get('email') ?? undefined;
  const isWaitlistApproved = searchParams.get('waitlist') === 'approved';
  const waitlistEnabled = configQuery.data?.waitlistEnabled ?? false;
  const showWaitlistForm = waitlistEnabled && !isWaitlistApproved;

  if (sessionStatus === 'loading' || providerQuery.isLoading || configQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const authProviderMetadata = providerQuery.data;

  if (authProviderMetadata && authProviderMetadata.id !== 'magic-link') {
    return <Navigate to="/auth/signin" replace />;
  }

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center p-6">
      <div className="absolute inset-0">
        <ColorBends
          colors={['#ffffff', '#888888', '#000000']}
          speed={0.14}
          scale={0.1}
          frequency={1.9}
          warpStrength={1.0}
          noise={0.21}
          centerRadius={0.55}
          centerFalloff={0.3}
          ascii
          asciiCellSize={5}
        />
      </div>

      <div className="absolute inset-0 bg-black/20" />

      <div className="relative z-10 w-full max-w-7xl mx-auto flex items-center justify-center gap-12">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md bg-card rounded-md p-8 shadow-2xl border border-ring/20"
        >
          <SharpeLogo size={36} className="mb-6" />

          <h1 className="text-3xl font-medium text-foreground mb-2">
            {showWaitlistForm ? 'Join the waitlist' : 'Create account'}
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            {showWaitlistForm
              ? "Sign up to get early access. We'll notify you when your spot is ready."
              : 'Already have an account?'}{' '}
            <Link
              href="/auth/signin"
              className="ml-1 text-foreground hover:text-foreground/70 font-medium no-underline"
            >
              Sign in
            </Link>
          </p>

          {!showWaitlistForm && initialEmail && (
            <div className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground mb-6">
              No account found for{' '}
              <span className="font-medium text-foreground">{initialEmail}</span>. Create one to
              continue.
            </div>
          )}

          {showWaitlistForm ? (
            <WaitlistForm initialEmail={initialEmail} />
          ) : (
            <MagicLinkForm mode="signup" initialEmail={initialEmail} />
          )}

          <p className="mt-6 text-center text-xs text-muted-foreground whitespace-nowrap">
            By creating an account, you agree to our{' '}
            <a
              href="https://sharpe.sh/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/70 hover:text-foreground underline underline-offset-2"
            >
              Terms of Use
            </a>{' '}
            and{' '}
            <a
              href="https://sharpe.sh/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/70 hover:text-foreground underline underline-offset-2"
            >
              Privacy Policy
            </a>
            .
          </p>
        </motion.div>
      </div>
    </div>
  );
}
